import React, { useCallback, useEffect, useRef, useState } from 'react';

const canvasConfig = {
  sketch: { width: 1200, height: 760, lineWidth: 5 },
  signature: { width: 1200, height: 320, lineWidth: 3 },
};

export const ClinicTouchCanvas = ({
  label,
  helper,
  value,
  onChange,
  testId,
  clearTestId,
  variant = 'sketch',
}) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasDrawing, setHasDrawing] = useState(Boolean(value));
  const config = canvasConfig[variant] || canvasConfig.sketch;

  const paintBase = useCallback((context) => {
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, config.width, config.height);
    context.strokeStyle = '#e2e8f0';
    context.lineWidth = 1;
    if (variant === 'sketch') {
      for (let y = 80; y < config.height; y += 120) {
        context.beginPath();
        context.moveTo(40, y);
        context.lineTo(config.width - 40, y);
        context.stroke();
      }
    } else {
      context.beginPath();
      context.moveTo(60, config.height - 90);
      context.lineTo(config.width - 60, config.height - 90);
      context.stroke();
    }
  }, [config.height, config.width, variant]);

  const hydrateCanvas = useCallback((dataUrl = '') => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    canvas.width = config.width;
    canvas.height = config.height;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = '#102a43';
    context.lineWidth = config.lineWidth;
    paintBase(context);

    if (!dataUrl) {
      setHasDrawing(false);
      return;
    }

    const image = new Image();
    image.onload = () => {
      paintBase(context);
      context.drawImage(image, 0, 0, config.width, config.height);
      setHasDrawing(true);
    };
    image.src = dataUrl;
  }, [config.height, config.lineWidth, config.width, paintBase]);

  useEffect(() => {
    hydrateCanvas(value || '');
  }, [hydrateCanvas, value]);

  const getPoint = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) * (canvas.width / rect.width),
      y: (event.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const finishStroke = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !drawingRef.current) return;
    drawingRef.current = false;
    setHasDrawing(true);
    onChange(canvas.toDataURL('image/png'));
  }, [onChange]);

  const handlePointerDown = (event) => {
    event.preventDefault();
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    const point = getPoint(event);
    drawingRef.current = true;
    canvas.setPointerCapture(event.pointerId);
    context.beginPath();
    context.moveTo(point.x, point.y);
  };

  const handlePointerMove = (event) => {
    if (!drawingRef.current) return;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    const point = getPoint(event);
    context.lineTo(point.x, point.y);
    context.stroke();
  };

  const clearCanvas = () => {
    hydrateCanvas('');
    onChange('');
  };

  return (
    <div className="rounded-[28px] border border-slate-200 bg-white p-4" data-testid={`${testId}-wrapper`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-900" data-testid={`${testId}-label`}>{label}</p>
          <p className="mt-1 text-xs text-slate-500" data-testid={`${testId}-helper`}>{helper}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${hasDrawing ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`} data-testid={`${testId}-status`}>
            {hasDrawing ? 'capturado' : 'pendiente'}
          </span>
          <button type="button" onClick={clearCanvas} className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600" data-testid={clearTestId}>
            Limpiar
          </button>
        </div>
      </div>
      <div className={`mt-4 overflow-hidden rounded-[24px] border border-slate-200 bg-slate-50 ${variant === 'signature' ? 'aspect-[16/5]' : 'aspect-[16/10]'}`}>
        <canvas
          ref={canvasRef}
          className="h-full w-full touch-none"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={finishStroke}
          onPointerLeave={finishStroke}
          onPointerCancel={finishStroke}
          data-testid={testId}
        />
      </div>
    </div>
  );
};
